import { Component, KeyboardEvent as ReactKeyboardEvent, MutableRefObject, ReactNode, Suspense, lazy, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FaXmark } from "react-icons/fa6";
import { SourceCitation, buildDocumentFileUrl, fetchDocumentFile } from "../../api/documentAgent";
import TextPreview from "./TextPreview";

// react-pdf + pdfjs-dist are by far the heaviest dependencies on this page
// and only PDF previews need them — loaded on demand so every other visitor
// never downloads them.
const PdfPreview = lazy(() => import("./PdfPreview"));

// The document a drawer instance is previewing: one document, with only ITS
// citations (the page groups citations by documentId before opening).
export type PreviewDocument = {
    documentId: string;
    documentTitle: string;
    documentFileName?: string | null;
    citations: SourceCitation[];
};

type SourcePreviewDrawerProps = {
    document: PreviewDocument | null;
    conversationId: string;
    // The page's staleness guard: captured before each await, compared after.
    // A mismatch means the session this fetch started under was expired or
    // cleared, and its result must be discarded.
    sessionEpochRef: MutableRefObject<number>;
    onClose: () => void;
};

type FileState =
    | { status: "loading" }
    | { status: "error"; message: string }
    | { status: "oversized" }
    | { status: "ready"; blob: Blob; contentType: string };

type ViewerKind = "pdf" | "text" | "passages";

const TEXT_EXTENSIONS = [".txt", ".md", ".csv", ".tsv", ".json", ".xml"];
// Above this size a text file is shown as passages instead of inline — a
// 2 MB+ wall of text helps nobody and can stall the tab.
const MAX_INLINE_TEXT_BYTES = 2_000_000;

function extensionOf(fileName?: string | null): string | null {
    if (!fileName || !fileName.includes(".")) return null;
    return `.${fileName.split(".").pop()!.toLowerCase()}`;
}

// Chooses the viewer from the filename extension, falling back to the
// fetched Content-Type when the filename is missing/extensionless.
//
// SECURITY: .html/.htm (and text/html) deliberately land on the passages
// view. This page's standing rule is that document-derived markup is never
// rendered live — no iframe, no innerHTML — so HTML files get their cited
// passages and a download link instead of a preview.
function viewerKindFor(fileName: string | null | undefined, contentType: string | undefined): ViewerKind {
    const extension = extensionOf(fileName);
    if (extension === ".pdf") return "pdf";
    if (extension && TEXT_EXTENSIONS.includes(extension)) return "text";
    if (extension) return "passages";

    const type = (contentType || "").toLowerCase();
    if (type.includes("application/pdf")) return "pdf";
    if (type.includes("text/html") || type.includes("application/xhtml")) return "passages";
    if (type.startsWith("text/") || type.includes("application/json") || type.includes("application/xml")) {
        return "text";
    }
    return "passages";
}

function formatPageLabel(citation: SourceCitation): string | null {
    return typeof citation.pageNumber === "number" ? `Page ${citation.pageNumber}` : null;
}

// Citation cards for formats without an in-browser preview, and the fallback
// when the original file can no longer be fetched.
function PassagesView({ citations, notice, downloadUrl, downloadName }: {
    citations: SourceCitation[];
    notice: string;
    downloadUrl?: string | null;
    downloadName?: string | null;
}) {
    return (
        <div className="h-full min-h-0 space-y-3 overflow-y-auto pr-1">
            <div role="status" className="rounded-md border border-primary_one-100 bg-primary_one-150 px-3 py-2 text-xs text-gray-300">
                {notice}
                {downloadUrl && (
                    <>
                        {" "}
                        <a
                            href={downloadUrl}
                            download={downloadName || true}
                            className="font-semibold text-primary_one-200 underline underline-offset-2 hover:text-red-300"
                        >
                            Download the original file
                        </a>
                    </>
                )}
            </div>
            {citations.map((citation, index) => (
                <div key={`${citation.chunkId}-${index}`} className="rounded-md border border-primary_one-100 bg-primary_one-150 px-4 py-3">
                    <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
                        {citation.sectionTitle && <span className="font-semibold text-gray-300">{citation.sectionTitle}</span>}
                        {formatPageLabel(citation) && <span>{formatPageLabel(citation)}</span>}
                    </div>
                    <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-200">{citation.quote}</p>
                </div>
            ))}
        </div>
    );
}

// Contains a crashed viewer to the drawer instead of blanking the page.
// The realistic trigger: pdfjs-dist 5 needs modern browser APIs
// (Promise.withResolvers and friends), so on an older browser the lazy PDF
// chunk can fail to even evaluate — the reader still gets the passages.
class ViewerErrorBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error: unknown) {
        // eslint-disable-next-line no-console
        console.error("Source preview viewer failed:", error);
    }

    render() {
        return this.state.failed ? this.props.fallback : this.props.children;
    }
}

export default function SourcePreviewDrawer({
    document: previewDocument,
    conversationId,
    sessionEpochRef,
    onClose,
}: SourcePreviewDrawerProps) {
    const [fileState, setFileState] = useState<FileState>({ status: "loading" });
    const [passageJump, setPassageJump] = useState<{ citationIndex: number; nonce: number } | null>(null);
    const closeButtonRef = useRef<HTMLButtonElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);
    // What the last fetch attempt was for and how it ended: a successful blob
    // for the same document is reused across in-place citation updates, but a
    // FAILED fetch is retried whenever the drawer is (re)opened or updated —
    // otherwise one transient error would pin the passages fallback forever.
    const lastFetchRef = useRef<{ documentId: string; succeeded: boolean } | null>(null);

    const documentId = previewDocument?.documentId ?? null;

    // Fetch the original file whenever a (new) document opens. Keyed on the
    // previewDocument identity so an in-place update can retry a failed
    // fetch; a same-document update after a success reuses the blob.
    useEffect(() => {
        if (!previewDocument) return undefined;
        const targetDocumentId = previewDocument.documentId;
        const last = lastFetchRef.current;
        if (last && last.documentId === targetDocumentId && last.succeeded) return undefined;

        let cancelled = false;
        const epoch = sessionEpochRef.current;
        setFileState({ status: "loading" });
        setPassageJump(null);

        fetchDocumentFile(targetDocumentId, conversationId)
            .then((result) => {
                if (cancelled || sessionEpochRef.current !== epoch) return;
                // Both outcomes here are successes, not failures — an
                // "oversized" reading is a real answer about the file the
                // HEAD probe just measured, not a fetch that broke, so it
                // gets the same "don't retry on the next open" treatment a
                // ready blob does.
                lastFetchRef.current = { documentId: targetDocumentId, succeeded: true };
                setFileState(result.status === "oversized"
                    ? { status: "oversized" }
                    : { status: "ready", blob: result.blob, contentType: result.contentType });
            })
            .catch((error: unknown) => {
                if (cancelled || sessionEpochRef.current !== epoch) return;
                lastFetchRef.current = { documentId: targetDocumentId, succeeded: false };
                const message = error instanceof Error && error.message
                    ? error.message
                    : "The original file could not be loaded.";
                setFileState({ status: "error", message });
            });

        return () => {
            cancelled = true;
        };
    }, [previewDocument, conversationId, sessionEpochRef]);

    // Install this once while the page is mounted. Attaching only after the
    // drawer opens leaves a small passive-effect gap in which the dialog is
    // already visible but Escape is still ignored (especially noticeable for
    // keyboard users and deterministic tests).
    const isOpenRef = useRef(Boolean(previewDocument));
    const onCloseRef = useRef(onClose);
    isOpenRef.current = Boolean(previewDocument);
    onCloseRef.current = onClose;
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && isOpenRef.current) onCloseRef.current();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    // Land keyboard focus inside the dialog when it opens, remember what had
    // focus before, and give it back on close — aria-modal without either is
    // a lie to screen-reader users.
    const wasOpenRef = useRef(false);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    useEffect(() => {
        if (documentId) {
            if (!wasOpenRef.current) {
                previouslyFocusedRef.current =
                    document.activeElement instanceof HTMLElement ? document.activeElement : null;
            }
            wasOpenRef.current = true;
            closeButtonRef.current?.focus();
        } else if (wasOpenRef.current) {
            wasOpenRef.current = false;
            previouslyFocusedRef.current?.focus();
            previouslyFocusedRef.current = null;
        }
    }, [documentId]);

    // Minimal Tab trap: aria-modal promises focus cannot wander into the
    // inert page behind the backdrop, so wrap at the panel's edges.
    const trapFocus = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Tab") return;
        const panel = panelRef.current;
        if (!panel) return;
        const focusables = panel.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (event.shiftKey) {
            if (active === first || !panel.contains(active)) {
                event.preventDefault();
                last.focus();
            }
        } else if (active === last || !panel.contains(active)) {
            event.preventDefault();
            first.focus();
        }
    };

    const renderViewer = () => {
        if (!previewDocument) return null;
        const { citations, documentFileName } = previewDocument;
        const downloadUrl = buildDocumentFileUrl(previewDocument.documentId, conversationId);

        if (fileState.status === "loading") {
            return <p className="py-6 text-center text-sm text-gray-400">Loading document preview…</p>;
        }

        if (fileState.status === "error") {
            return (
                <PassagesView
                    citations={citations}
                    notice={`The original document could not be loaded (${fileState.message}) — it may have been cleared or replaced. The cited passages are shown below.`}
                />
            );
        }

        // The oversized reading never fetched a blob at all — there is no
        // contentType to route a viewer on, and holding a file this large in
        // memory to preview a handful of cited passages would be the wrong
        // trade anyway. Same fallback shape a format with no in-browser
        // viewer gets: cited passages plus a real download link.
        if (fileState.status === "oversized") {
            return (
                <PassagesView
                    citations={citations}
                    notice="This file is too large to preview in the browser, so the cited passages are shown instead."
                    downloadUrl={downloadUrl}
                    downloadName={documentFileName ?? null}
                />
            );
        }

        const kind = viewerKindFor(documentFileName, fileState.contentType);

        const viewerFallback = (
            <PassagesView
                citations={citations}
                notice="The in-browser viewer could not be loaded, so the cited passages are shown instead."
                downloadUrl={downloadUrl}
                downloadName={documentFileName ?? null}
            />
        );

        if (kind === "pdf") {
            return (
                <ViewerErrorBoundary fallback={viewerFallback}>
                    <Suspense fallback={<p className="py-6 text-center text-sm text-gray-400">Loading PDF viewer…</p>}>
                        <PdfPreview blob={fileState.blob} citations={citations} passageJump={passageJump} />
                    </Suspense>
                </ViewerErrorBoundary>
            );
        }

        if (kind === "text" && fileState.blob.size <= MAX_INLINE_TEXT_BYTES) {
            return (
                <ViewerErrorBoundary fallback={viewerFallback}>
                    <TextPreview blob={fileState.blob} citations={citations} passageJump={passageJump} />
                </ViewerErrorBoundary>
            );
        }

        return (
            <PassagesView
                citations={citations}
                notice={kind === "text"
                    ? "This file is too large to preview inline, so the cited passages are shown instead."
                    : "This file format has no in-browser preview, so the cited passages are shown instead."}
                downloadUrl={downloadUrl}
                downloadName={documentFileName ?? null}
            />
        );
    };

    // Closing must remove both fixed layers synchronously. Exit animations are
    // deliberately not used here: if an animation frame is suspended (a
    // background tab, reduced-motion environment, or jsdom), AnimatePresence
    // can retain the invisible backdrop and dialog indefinitely, swallowing
    // every click on the page. The entrance remains animated; the close path
    // is deterministic and immediately restores access to the page beneath it.
    if (!previewDocument) return null;

    return (
        <>
            <motion.div
                key="source-preview-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                onClick={onClose}
                aria-hidden="true"
                className="fixed inset-0 z-50 bg-black/50"
            />
            <motion.div
                key="source-preview-panel"
                ref={panelRef}
                onKeyDown={trapFocus}
                role="dialog"
                aria-modal="true"
                aria-label={`Source preview: ${previewDocument.documentTitle}`}
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
                className="fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-primary_one-100 bg-primary_one-140 text-primary_one-50 shadow-2xl sm:w-[min(92vw,600px)]"
            >
                        <div className="flex flex-shrink-0 items-start justify-between gap-3 border-b border-primary_one-100 px-5 py-4">
                            <div className="min-w-0">
                                <h3 className="truncate text-lg font-bold" title={previewDocument.documentTitle}>
                                    {previewDocument.documentTitle}
                                </h3>
                                {previewDocument.documentFileName && (
                                    <p className="truncate text-xs text-gray-400" title={previewDocument.documentFileName}>
                                        {previewDocument.documentFileName}
                                    </p>
                                )}
                            </div>
                            <button
                                ref={closeButtonRef}
                                type="button"
                                onClick={onClose}
                                aria-label="Close source preview"
                                className="flex-shrink-0 rounded p-1.5 text-gray-400 transition-colors hover:bg-primary_one-150 hover:text-gray-200"
                            >
                                <FaXmark aria-hidden="true" />
                            </button>
                        </div>

                        <div className="min-h-0 flex-1 px-5 py-4">
                            {renderViewer()}
                        </div>

                        <div className="max-h-[35%] flex-shrink-0 overflow-y-auto border-t border-primary_one-100 px-5 py-3">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                Cited passages
                            </p>
                            <ul className="space-y-1.5">
                                {previewDocument.citations.map((citation, index) => (
                                    <li key={`${citation.chunkId}-${index}`}>
                                        <button
                                            type="button"
                                            onClick={() => setPassageJump((current) => ({
                                                citationIndex: index,
                                                nonce: (current?.nonce ?? 0) + 1,
                                            }))}
                                            className="w-full rounded-md border border-primary_one-100 bg-primary_one-150 px-3 py-2 text-left text-xs transition-colors hover:border-primary_one-200"
                                        >
                                            <span className="mb-0.5 flex flex-wrap items-center gap-x-2 text-gray-400">
                                                {citation.sectionTitle && (
                                                    <span className="font-semibold text-gray-300">{citation.sectionTitle}</span>
                                                )}
                                                {formatPageLabel(citation) && <span>{formatPageLabel(citation)}</span>}
                                            </span>
                                            <span className="line-clamp-2 block break-words text-gray-200">
                                                {citation.quote}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
            </motion.div>
        </>
    );
}
